import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Check, X, Lock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { act } from "@/lib/hooks";
import { fdatetime } from "@/lib/api";
import { portalApi, usePortalLoad, downloadWith } from "@/lib/portalApi";
import { PageHeader, Section, StatusBadge } from "@/components/common";
import { FileUploadButton, fsize } from "@/components/FileUploadButton";
import { StageTracker } from "./PortalJobs";

export function ArtworkList({ rows, client, base, testId = "artwork-list", onDelete }) {
  return (
    <ul className="divide-y divide-slate-100" data-testid={testId}>
      {(rows || []).length === 0 && <li className="px-5 py-4 text-sm text-slate-400">No artwork uploaded yet.</li>}
      {(rows || []).map((a) => (
        <li key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm" data-testid={`artwork-v${a.version}`}>
          <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold">v{a.version}</span>
          <div className="min-w-0 flex-1"><div className="truncate font-medium text-slate-900">{a.filename}</div>
            <div className="text-xs text-slate-500">{fsize(a.size || 0)} · {a.uploaded_by_name || "—"} ({a.uploaded_by_type}) · {fdatetime(a.created_at)}{a.note && ` · ${a.note}`}</div></div>
          <Button size="sm" variant="ghost" data-testid={`artwork-download-v${a.version}`} onClick={() => downloadWith(client, `${base}/artwork/${a.id}/file`, a.filename)}><Download className="h-4 w-4" /></Button>
          {onDelete && <Button size="sm" variant="ghost" data-testid={`artwork-delete-v${a.version}`} onClick={() => onDelete(a)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
        </li>
      ))}
    </ul>
  );
}

function ProofRow({ p, onDecide }) {
  return (
    <li className="space-y-2 px-5 py-4 text-sm" data-testid={`portal-proof-v${p.version}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold">Proof v{p.version}</span>
        <StatusBadge status={p.status === "approved" ? "paid" : p.status === "rejected" ? "failed" : "pending"} label={p.status_label} testId={`portal-proof-status-v${p.version}`} />
        {p.locked && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" />Locked</span>}
        <span className="flex-1 truncate text-slate-600">{p.filename} · {fsize(p.size)}</span>
        <Button size="sm" variant="outline" data-testid={`portal-proof-download-v${p.version}`} onClick={() => downloadWith(portalApi, `/proofs/${p.id}/file`, p.filename)}><Download className="mr-1 h-4 w-4" />View proof</Button>
      </div>
      {p.note && <p className="text-xs text-slate-500">Note from printer: {p.note}</p>}
      {p.customer_comment && <p className="text-xs text-rose-700">Your comment: {p.customer_comment}</p>}
      {p.status === "sent" && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" data-testid={`portal-proof-approve-v${p.version}`} onClick={() => onDecide(p, "approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check className="mr-1 h-4 w-4" />Approve proof</Button>
          <Button size="sm" variant="outline" data-testid={`portal-proof-reject-v${p.version}`} onClick={() => onDecide(p, "reject")} className="border-rose-300 text-rose-700 hover:bg-rose-50"><X className="mr-1 h-4 w-4" />Request changes</Button>
        </div>
      )}
    </li>
  );
}

export default function PortalJob() {
  const { id } = useParams();
  const [d, reload] = usePortalLoad(`/jobs/${id}`);
  const [dec, setDec] = useState(null);
  const [comment, setComment] = useState("");
  if (!d) return null;
  const j = d.job;
  const attach = async (upload_id) => { if (await act(() => portalApi.post("/artwork", { upload_id, target_type: "job", target_id: id }), "Artwork uploaded")) reload(); };
  const confirm = async () => {
    const r = await act(() => portalApi.post(`/proofs/${dec.p.id}/${dec.kind}`, dec.kind === "reject" ? { comment } : {}), dec.kind === "approve" ? "Proof approved" : "Changes requested");
    if (r) { setDec(null); setComment(""); reload(); }
  };
  return (
    <>
      <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="portal-back-jobs"><ArrowLeft className="h-4 w-4" />All jobs</Link>
      <PageHeader title={j.title} subtitle={`${j.number}${j.quote_number ? ` · from quote ${j.quote_number}` : ""} · qty ${j.quantity?.toLocaleString()}`} />
      <div className="po-card space-y-4 p-5" data-testid="portal-job-status-card">
        <div className="flex items-center justify-between"><span className="po-label">Current stage</span><span data-testid="portal-job-label" className="text-lg font-semibold" style={{ color: "var(--brand)" }}>{j.label}</span></div>
        <StageTracker stages={d.stages} stage={j.stage} />
        <div className="hidden justify-between text-[11px] text-slate-500 sm:flex">{d.stages.map((s) => <span key={s} className="flex-1 text-center">{s}</span>)}</div>
      </div>
      <Section title="Proofs" testId="portal-proofs-section"><ul className="divide-y divide-slate-100">
        {d.proofs.length === 0 && <li className="px-5 py-4 text-sm text-slate-400">No proofs sent yet. We'll notify you when one is ready.</li>}
        {d.proofs.map((p) => <ProofRow key={p.id} p={p} onDecide={(p, kind) => setDec({ p, kind })} />)}
      </ul></Section>
      <Section title="Your artwork" testId="portal-artwork-section" action={<FileUploadButton client={portalApi} base="" onUploaded={attach} label="Upload artwork" testId="portal-artwork-upload" />}>
        <ArtworkList rows={d.artwork} client={portalApi} base="" testId="portal-artwork-list" />
      </Section>
      <Dialog open={!!dec} onOpenChange={() => setDec(null)}>
        <DialogContent data-testid="proof-decision-dialog">
          <DialogHeader><DialogTitle>{dec?.kind === "approve" ? `Approve proof v${dec?.p.version}?` : `Request changes to proof v${dec?.p.version}`}</DialogTitle>
            <DialogDescription>{dec?.kind === "approve" ? "Approving locks this proof and lets production start. This cannot be undone." : "Tell us what needs to change. We'll send a revised proof."}</DialogDescription></DialogHeader>
          {dec?.kind === "reject" && <Textarea data-testid="proof-reject-comment" placeholder="What should we change? (required)" value={comment} onChange={(e) => setComment(e.target.value)} />}
          <DialogFooter><Button data-testid="proof-decision-confirm" disabled={dec?.kind === "reject" && comment.trim().length < 3} onClick={confirm}>{dec?.kind === "approve" ? "Approve" : "Send feedback"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
